import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { clampWhenHidden, POLL_MACHINE_MS } from "@/lib/realtime";
import { livePoll, useCalendarLive } from "./calendarLive";
import type { CustomMedia } from "@/types/contentCalendar.type";

export const customMediaKey = (brandId: string, mediaId: string | null) =>
  ["content-calendar", brandId, "custom-media", mediaId] as const;

/**
 * One pasted link's save, polled while its files are still being mirrored.
 *
 * READY and FAILED are terminal for a row — a retry is a new paste, which makes
 * a new row — so polling stops there and only there.
 */
export function useCustomMedia(brandId: string, mediaId: string | null) {
  const live = useCalendarLive();
  return useQuery({
    queryKey: customMediaKey(brandId, mediaId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ media: CustomMedia }>(
        `/content-calendar/${brandId}/custom-media/${mediaId}`,
      );
      return res.data.media;
    },
    enabled: !!brandId && !!mediaId,
    refetchInterval: (query) =>
      livePoll(live, query.state.data?.status === "PROCESSING" ? clampWhenHidden(POLL_MACHINE_MS) : false),
  });
}

/**
 * Start saving a pasted link. The server answers at once — PROCESSING, or READY
 * when this brand already saved the same post — and the caller follows the row
 * with `useCustomMedia`. Seeding the cache here means that first poll is free.
 */
export function useCreateCustomMedia(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (url: string) => {
      const res = await axiosInstance.post<{ media: CustomMedia }>(
        `/content-calendar/${brandId}/custom-media`,
        { url },
      );
      return res.data.media;
    },
    onSuccess: (media) => qc.setQueryData(customMediaKey(brandId, media.id), media),
  });
}
